import { IoArrowBackOutline, IoPlay } from "react-icons/io5";
import {AiFillStar} from 'react-icons/ai';
import {PiUsersThreeFill} from 'react-icons/pi';
import {HiOutlineCalendar, HiOutlineClock, HiOutlineGlobeAlt} from 'react-icons/hi';
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Hearts } from 'react-loader-spinner';

const ShowContentCard = () => {
    const { id, type } = useParams();
    const [content, setContent] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const [credits, setCredits] = useState(null);
    const [watchProviders, setWatchProviders] = useState(null);

    const fetchCredits = async () => {
        try {
            const response = await fetch(`https://api.frazza.online/tv/${id}/credits`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setCredits(data);
        } catch (error) {
            console.error('Credits fetch error:', error);
        }
    };

    const fetchWatchProviders = async () => {
        try {
            const response = await fetch(`https://api.frazza.online/content/tv/${id}/watch-providers`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setWatchProviders(data.results?.US?.flatrate || []);
        } catch (error) {
            console.error('Watch providers fetch error:', error);
        }
    };

    useEffect(() => {
        if (content) {
            fetchCredits();
            fetchWatchProviders();
        }
    }, [content])

    const fetchContentDetails = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await fetch(`https://api.frazza.online/tv/${id}`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setContent(data);

        } catch (error) {
            console.error('Fetch error:', error);
            setError(`Failed to load ${type} details: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchContentDetails();
    }, [id, type]);

    if (isLoading) return <div className="content-loading"><Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} /><p>Loading content...</p></div>;
    if (error) return <div className="content-error"><IoArrowBackOutline onClick={() => navigate(-1)} /><h2>Error</h2><p>{error}</p></div>;
    if (!content) return <div className="content-not-found"><IoArrowBackOutline onClick={() => navigate(-1)} /><h2>Content Not Found</h2></div>;

    const year = content.first_air_date ? content.first_air_date.split('-')[0] : '—';
    const runtime = content.episode_run_time?.length ? `${content.episode_run_time[0]}m/ep` : null;
    const companies = content.production_companies?.map(c => c.name) || [];
    const countries = content.production_countries?.map(c => c.name) || [];
    const genres = content.genres?.map(g => g.name) || [];
    const castList = credits?.cast?.slice(0,12) || [];

    const handleStartWatching = () => {
        const providerUrls = {
            8: 'https://www.netflix.com',
            9: 'https://www.amazon.com/primevideo',
            337: 'https://www.disneyplus.com',
            350: 'https://tv.apple.com'
        };

        if (watchProviders && watchProviders.length > 0) {
            const providerId = watchProviders[0].provider_id;
            const url = providerUrls[providerId];
            if (url) {
                window.open(url, '_blank');
            }
        }
    };

    return (
        <div className="content-page">
            <section className="hero-section" style={{
                backgroundImage: content.backdrop_path ? `linear-gradient(100deg, #7a0808 0%, #9d1010 40%, rgba(125,0,0,.95) 70%), url(https://image.tmdb.org/t/p/original${content.backdrop_path})` : undefined
            }}>
                <div className="hero-inner">
                    <button className="hero-back" onClick={() => navigate(-1)} aria-label="Go back"><IoArrowBackOutline/></button>
                    <div className="hero-grid">
                        <div className="poster-frame">
                            <img className="poster-img" src={`https://image.tmdb.org/t/p/w500${content.poster_path}`} alt={content.name} />
                        </div>
                        <div className="hero-meta">
                            <h1 className="hero-title">{content.name}</h1>
                            {content.tagline && <p className="hero-tagline">{content.tagline}</p>}
                            <div className="stats-row">
                                <span className="stat"><AiFillStar className="icon star"/> {content.vote_average.toFixed(1)} <span className="of">/10</span></span>
                                <span className="stat"><PiUsersThreeFill className="icon"/> {content.vote_count} votes</span>
                                {year && <span className="stat"><HiOutlineCalendar className="icon"/> {year}</span>}
                                {runtime && <span className="stat"><HiOutlineClock className="icon"/> {runtime}</span>}
                            </div>
                            <div className="lang-row"><HiOutlineGlobeAlt className="icon"/> {content.original_language?.toUpperCase()}</div>
                            <div className="genre-badges">
                                {genres.map(g => <span key={g} className="genre-pill">{g}</span>)}
                            </div>
                            {watchProviders && watchProviders.length > 0 && (
                                <button className="primary-action-btn" onClick={handleStartWatching}>
                                    <IoPlay/> Start Watching
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <main className="content-sections">
                <div className="section-card overview-card">
                    <h2>Overview</h2>
                    <p className="overview-text">{content.overview}</p>
                    {content.overview && <div className="highlight-box">Binge-worthy and full of heart. Share the journey together episode by episode.</div>}
                </div>
                <div className="info-grid">
                    <div className="section-card">
                        <h2>Production Companies</h2>
                        {companies.length ? companies.map(c => <div key={c}>{c}</div>) : <div>—</div>}
                    </div>
                    <div className="section-card">
                        <h2>Production Countries</h2>
                        {countries.length ? countries.map(c => <div key={c}>{c}</div>) : <div>—</div>}
                    </div>
                </div>
                {castList.length > 0 && (
                    <div className="section-card cast-card">
                        <h2>Cast</h2>
                        <div className="cast-grid">
                            {castList.map(person => (
                                <div className="cast-item" key={person.id}>
                                    <img src={person.profile_path ? `https://image.tmdb.org/t/p/w185${person.profile_path}` : ''} alt={person.name} />
                                    <div className="cast-names">
                                        <p className="actor">{person.name}</p>
                                        <p className="character">{person.character}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default ShowContentCard;